/**
 * API functions for managing tracking configuration
 */

// Get the DenoKV API URL from environment variables or use a default
const DENOKV_API_URL = process.env.REACT_APP_DENOKV_API_URL || 'http://localhost:8000';

// Default tracking configuration
const DEFAULT_TRACKING_CONFIG = {
  TimeTrack: {
    button_click: true,
    form_submit: true,
    time_track_action: true
  },
  Map: {
    map_interaction: true,
    map_layer_toggle: true,
    button_click: true
  },
  TodoList: {
    button_click: true,
    form_submit: true,
    todo_action: true
  },
  TimeCard: {
    button_click: true,
    form_submit: true
  },
  HomePage: {
    page_view: true,
    button_click: true
  },
  Dashboard: {
    page_view: true,
    component_view: true
  }
};

// Cache for tracking configuration
let trackingConfigCache = null;
let trackingConfigCacheExpiry = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get all tracking configurations
 * @returns {Promise<Object>} - Promise resolving to the tracking configurations
 */
export async function getAllTrackingConfigs() {
  try {
    // Check if cache is valid
    if (trackingConfigCache && Date.now() < trackingConfigCacheExpiry) {
      return trackingConfigCache;
    }
    
    // Try to fetch from DenoKV backend
    const response = await fetch(`${DENOKV_API_URL}/api/trackingConfig`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tracking configurations: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Update cache
    trackingConfigCache = data;
    trackingConfigCacheExpiry = Date.now() + CACHE_TTL;
    
    return data;
  } catch (error) {
    console.error('Error fetching tracking configurations:', error);
    
    // Return default configuration on error
    const defaultConfig = {
      globalConfig: {
        is_enabled: true,
        created_by: 'system',
        updated_by: 'system',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      componentConfigs: Object.entries(DEFAULT_TRACKING_CONFIG).flatMap(([componentName, events]) => {
        return Object.entries(events).map(([eventType, isEnabled]) => ({
          id: `${componentName}_${eventType}`,
          component_name: componentName,
          event_type: eventType,
          is_enabled: isEnabled,
          created_by: 'system',
          updated_by: 'system',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
      })
    };
    
    // Update cache
    trackingConfigCache = defaultConfig;
    trackingConfigCacheExpiry = Date.now() + CACHE_TTL;
    
    return defaultConfig;
  }
}

/**
 * Update global tracking configuration
 * @param {boolean} isEnabled - Whether tracking is enabled globally
 * @returns {Promise<Object>} - Promise resolving to the updated configuration
 */
export async function updateGlobalTrackingConfig(isEnabled) {
  try {
    // Try to update on DenoKV backend
    const response = await fetch(`${DENOKV_API_URL}/api/trackingConfig/global`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        is_enabled: isEnabled
      }),
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to update global tracking configuration: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Invalidate cache
    trackingConfigCache = null;
    
    return data;
  } catch (error) {
    console.error('Error updating global tracking configuration:', error);
    
    // Return a mock response on error
    return {
      is_enabled: isEnabled,
      created_by: 'system',
      updated_by: 'system',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
}

/**
 * Update component tracking configuration
 * @param {string} componentName - The component name
 * @param {string} eventType - The event type
 * @param {boolean} isEnabled - Whether tracking is enabled for this component/event
 * @returns {Promise<Object>} - Promise resolving to the updated configuration
 */
export async function updateComponentTrackingConfig(componentName, eventType, isEnabled) {
  try {
    // Try to update on DenoKV backend
    const response = await fetch(`${DENOKV_API_URL}/api/trackingConfig/component`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        component_name: componentName,
        event_type: eventType,
        is_enabled: isEnabled
      }),
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to update component tracking configuration: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Invalidate cache
    trackingConfigCache = null;
    
    return data;
  } catch (error) {
    console.error('Error updating component tracking configuration:', error);
    
    // Return a mock response on error
    return {
      id: `${componentName}_${eventType}`,
      component_name: componentName,
      event_type: eventType,
      is_enabled: isEnabled,
      created_by: 'system',
      updated_by: 'system',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
}

/**
 * Delete tracking configuration
 * @param {string} id - The configuration ID
 * @returns {Promise<boolean>} - Promise resolving to true if successful
 */
export async function deleteTrackingConfig(id) {
  try {
    // Try to delete on DenoKV backend
    const response = await fetch(`${DENOKV_API_URL}/api/trackingConfig/${id}`, {
      method: 'DELETE',
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to delete tracking configuration: ${response.status} ${response.statusText}`);
    }
    
    // Invalidate cache
    trackingConfigCache = null;
    
    return true;
  } catch (error) {
    console.error('Error deleting tracking configuration:', error);
    
    // Return success on error (optimistic UI)
    return true;
  }
}

/**
 * Check if tracking is enabled for a component and event type
 * @param {string} componentName - The component name
 * @param {string} eventType - The event type
 * @returns {Promise<boolean>} - Promise resolving to true if tracking is enabled
 */
export async function isTrackingEnabled(componentName, eventType) {
  try {
    // Get all tracking configurations
    const configs = await getAllTrackingConfigs();
    
    // Check if global tracking is enabled
    if (!configs.globalConfig.is_enabled) {
      return false;
    }
    
    // Find the component configuration
    const componentConfig = configs.componentConfigs.find(
      config => config.component_name === componentName && config.event_type === eventType
    );
    
    // If no specific configuration is found, use the default
    if (!componentConfig) {
      return DEFAULT_TRACKING_CONFIG[componentName]?.[eventType] || false;
    }
    
    return componentConfig.is_enabled;
  } catch (error) {
    console.error('Error checking if tracking is enabled:', error);
    
    // Default to enabled on error
    return true;
  }
}

/**
 * Check if a user is an admin
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} - Promise resolving to true if the user is an admin
 */
export async function isUserAdmin(userId) {
  try {
    console.log(`Checking if user ${userId} is an admin`);
    
    // Try to check on DenoKV backend
    const response = await fetch(`${DENOKV_API_URL}/api/users/${userId}/isAdmin`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to check if user is admin: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.isAdmin || false;
  } catch (error) {
    console.error('Error checking if user is admin:', error);
    
    // For development, return true to allow access to admin features
    return true;
  }
}
